import { inject, InjectionToken } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  combineLatestWith,
  distinctUntilChanged,
  firstValueFrom,
  from,
  of,
  pipe,
  switchMap,
} from 'rxjs';
import { AliasService } from 'src/app/authentication/alias/alias.service';
import { getIsNullable } from 'src/app/core/nullable-utils';
import { patchObjectsShallow } from 'src/app/core/object-utils';
import { components } from 'src/app/core/services/api/generated/ix-api.types';
import { ProjectId } from 'src/app/core/state/project.store';
import { NotificationApiService } from '../notification-api.service';

export type NotificationEntryDto = components['schemas']['NotificationEntryDto'];
export type NotificationEntryInputDto = components['schemas']['NotificationEntryInputDto'];

interface NotificationState {
  notifications: NotificationEntryDto[];
  isLoading: boolean;
}

const NotificationInitialState: NotificationState = {
  notifications: [],
  isLoading: false,
};

const NotificationStateToken = new InjectionToken<NotificationState>('NotificationState', {
  factory: () => NotificationInitialState,
});

export const NotificationStore = signalStore(
  { providedIn: 'root' },
  withState(() => inject(NotificationStateToken)),
  withMethods((store, apiService = inject(NotificationApiService)) => ({
    async loadAll(projectId: ProjectId): Promise<void> {
      patchState(store, { isLoading: true });

      const notifications = await firstValueFrom(
        getIsNullable(projectId) ? of([]) : apiService.getNotifications({ projectId }, {}),
      );

      patchState(store, { notifications, isLoading: false });
    },
    async updateNotifications({
      projectId,
      patches,
    }: {
      projectId: number;
      patches: NotificationEntryInputDto[];
    }): Promise<void> {
      const previousNotifications = store.notifications();
      const nextNotification = patchObjectsShallow({ objects: previousNotifications, patches, id: 'id' });

      patchState(store, { notifications: nextNotification }); // optimistic update

      await firstValueFrom(
        apiService.updateNotifications({ projectId }, patches).pipe(
          catchError(() => {
            patchState(store, { notifications: previousNotifications });
            return of([]);
          }),
        ),
      );
    },
  })),
  withMethods((store, aliasService = inject(AliasService)) => ({
    loadByProjectId: rxMethod<ProjectId>(
      pipe(
        distinctUntilChanged(),
        combineLatestWith(toObservable(aliasService.alias)),
        switchMap(([projectId]) => from(store.loadAll(projectId))),
      ),
    ),
  })),
);
